// Persistent очередь недоставленных аудио-чанков записи лекций.
//
// Причина существования: до 2026-07-28 чанк после MAX_CHUNK_RETRY неудачных
// попыток аплоада молча выбрасывался (console.error) — кусок лекции терялся
// навсегда, пользователь не узнавал. Теперь чанк кладётся в IndexedDB и
// доставляется фоновым drain'ом:
//   - по событию `online` (network port / общая проба);
//   - по интервалу (30s);
//   - при finalize — stop() ждёт опустошения очереди (grace-окно), потому что
//     после finalize сервер чанки НЕ принимает (uploadChunk: status guard);
//   - при повторном входе в приложение (recovery хвоста прошлой сессии).
//
// Хранение, межвкладочная блокировка, online-проба и фильтр владельца — в общем
// движке `lib/idb-outbox.ts` (там же объяснено, почему их не может не быть).
// Здесь остаётся только специфика чанков: порядок доставки, ожидание пустой
// очереди перед finalize и продолжение нумерации после reload.
//
// Идемпотентность: unique (recordingId, sequence) + confirmChunk no-op на
// сервере → повторная доставка безопасна. Ключ: `chunk:{recordingId}:{sequence}`.
//
// Модуль намеренно не знает про tRPC/Zustand: upload и детект permanent-ошибок
// приходят колбэками из use-recorder (hooks/ → lib/ по boundaries).

import { createIdbOutbox } from "@/lib/idb-outbox";
import { getLastKnownOnline } from "@/lib/network-status";

/** Чанк, ожидающий доставки. Blob переживает reload (structured clone в IDB). */
export interface QueuedChunk {
	recordingId: string;
	sequence: number;
	mimeType: string;
	blob: Blob;
	enqueuedAt: number;
	/**
	 * Владелец чанка. Без него после смены аккаунта чужие чанки уходили заново
	 * (спасал только серверный status-guard). Поле опционально ради записей,
	 * лежащих в IDB с прошлых версий — они достаются текущему пользователю.
	 */
	userId?: string;
}

export interface ChunkUploadQueueDeps {
	/** Владелец очереди — дренить только его чанки. */
	userId: string | undefined;
	/** Одна попытка доставки (uploadChunk → presigned PUT → confirmChunk). */
	upload: (chunk: QueuedChunk) => Promise<void>;
	/** true = сервер чанк не примет никогда (запись финализирована/удалена). */
	isPermanentError: (err: unknown) => boolean;
	/** Счётчик очереди → recorder store (бейдж в UI). */
	onCountChange: (count: number) => void;
	/** Чанки выброшены после permanent-ошибки — видимая (не молчаливая) потеря. */
	onDropped: (dropped: number) => void;
	/** Очередь записи опустела — триггер auto-finalize, если stop() её ждал. */
	onQueueEmptied: (recordingId: string) => void;
}

export interface ChunkUploadQueue {
	enqueue: (chunk: Omit<QueuedChunk, "userId">) => Promise<void>;
	/** Одна волна доставки всей очереди. Reentrancy-safe + межвкладочный lock. */
	drain: () => Promise<void>;
	/**
	 * Крутить drain, пока очередь записи не опустеет или не истечёт graceMs.
	 * false = чанки остались (офлайн) — финализировать нельзя.
	 */
	drainUntilEmpty: (recordingId: string, graceMs: number) => Promise<boolean>;
	/** Сколько чанков конкретной записи ещё ждут доставки. */
	countForRecording: (recordingId: string) => Promise<number>;
	/**
	 * Макс. sequence чанка записи в IDB-очереди, либо null если очереди нет.
	 * Нужен для continue после reload — новый capture не должен пересечься
	 * с ещё недоставленными sequence.
	 */
	maxSequenceForRecording: (recordingId: string) => Promise<number | null>;
	/** Удалить все чанки записи (cancel/delete). */
	dropForRecording: (recordingId: string) => Promise<void>;
	/** Перечитать счётчик из IDB → onCountChange (recovery после reload). */
	refreshCount: () => Promise<void>;
	/** Online-listener + интервал + recovery-drain. Возвращает cleanup. */
	startBackground: () => () => void;
}

const DRAIN_ROUND_PAUSE_MS = 1_500;

const keyFor = (recordingId: string, sequence: number): string => `${recordingId}:${sequence}`;

export function createChunkUploadQueue(deps: ChunkUploadQueueDeps): ChunkUploadQueue {
	const outbox = createIdbOutbox<QueuedChunk>({
		// Отдельная БД от pending-finalize: idb-keyval не умеет два store в одной
		// БД без ручного version bump (иначе «object store was not found»).
		dbName: "studyhub-recording-chunk-queue",
		storeName: "pending-chunks",
		keyPrefix: "chunk:",
		lockName: "studyhub-recording-chunk-queue-drain",
		keyOf: (c) => keyFor(c.recordingId, c.sequence),
		ownerOf: (c) => c.userId,
		userId: deps.userId,
		// FIFO по времени постановки, внутри записи — по sequence.
		compare: (a, b) =>
			a.recordingId === b.recordingId ? a.sequence - b.sequence : a.enqueuedAt - b.enqueuedAt,
		send: deps.upload,
		isPermanentError: deps.isPermanentError,
		// Чанки независимы: сбой одного не должен блокировать доставку остальных
		// (в отличие от сообщений репетитора, где порядок — часть смысла).
		stopOnTransientError: false,
		onCountChange: deps.onCountChange,
		onDrained: async ({ sent, dropped }) => {
			if (dropped.length > 0) {
				for (const { item, err } of dropped) {
					// eslint-disable-next-line no-console
					console.error("recording-upload-queue: chunk rejected permanently, dropping", {
						recordingId: item.recordingId,
						sequence: item.sequence,
						err,
					});
				}
				deps.onDropped(dropped.length);
			}
			const touched = new Set([...sent, ...dropped.map((d) => d.item)].map((c) => c.recordingId));
			for (const recordingId of touched) {
				if ((await outbox.count((c) => c.recordingId === recordingId)) === 0) {
					deps.onQueueEmptied(recordingId);
				}
			}
		},
	});

	const countFor = (recordingId: string): Promise<number> =>
		outbox.count((c) => c.recordingId === recordingId);

	const drainUntilEmpty = async (recordingId: string, graceMs: number): Promise<boolean> => {
		const deadline = Date.now() + graceMs;
		for (;;) {
			await outbox.drain();
			if ((await countFor(recordingId)) === 0) return true;
			// Офлайн — ждать бессмысленно, finalize откладывается сразу. Берём
			// последнюю известную оценку, а не новую пробу: цикл тесный.
			if (getLastKnownOnline() === false || Date.now() >= deadline) return false;
			await new Promise<void>((resolve) => setTimeout(resolve, DRAIN_ROUND_PAUSE_MS));
		}
	};

	return {
		enqueue: (chunk) => outbox.put({ ...chunk, userId: deps.userId }),
		drain: outbox.drain,
		drainUntilEmpty,
		countForRecording: countFor,
		maxSequenceForRecording: async (recordingId) => {
			const items = await outbox.list();
			let max: number | null = null;
			for (const c of items) {
				if (c.recordingId !== recordingId) continue;
				if (max === null || c.sequence > max) max = c.sequence;
			}
			return max;
		},
		dropForRecording: async (recordingId) => {
			await outbox.removeWhere((key) => key.startsWith(`${recordingId}:`));
		},
		refreshCount: outbox.refreshCount,
		startBackground: outbox.startBackground,
	};
}
